import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    alpha,
    CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { buildApiUrl } from "../../../config/api";
import { getSchoolIdForUser } from "../../../utils/userContext";
import Popup from "../../../components/Popup";
import ConfirmDeleteDialog from "../../../components/ConfirmDeleteDialog";

const FACILITY_TYPES = ["Classroom", "Laboratory", "Dormitory", "Office", "Library", "Sports", "Other"];
const FACILITY_STATUSES = ["Active", "UnderMaintenance", "Inactive"];
const ASSET_CATEGORIES = ["Furniture", "Equipment", "Computer", "Vehicle", "Other"];
const ASSET_CONDITIONS = ["Good", "Fair", "Poor", "Broken", "Disposed"];
const PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const MAINT_STATUSES = ["Open", "InProgress", "Completed", "Cancelled"];

const EMPTY_FACILITY = {
    name: "",
    facilityType: "Classroom",
    building: "",
    floor: "",
    capacity: "",
    status: "Active",
    description: "",
    notes: "",
};

const EMPTY_ASSET = {
    name: "",
    assetTag: "",
    category: "Equipment",
    condition: "Good",
    quantity: "1",
    facilityId: "",
    purchaseDate: "",
    purchaseValue: "",
    manufacturer: "",
    warrantyUntil: "",
    description: "",
};

const EMPTY_MAINT = {
    title: "",
    description: "",
    priority: "Medium",
    facilityId: "",
    assetId: "",
    assignedTo: "",
};

function Kpi({ label, value, sub, alert: isAlert }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 2,
                border: (t) => `1px solid ${isAlert ? t.palette.warning.main : t.palette.divider}`,
                height: "100%",
                bgcolor: isAlert ? (t) => alpha(t.palette.warning.main, 0.06) : undefined,
            }}
        >
            <Typography variant="overline" color="text.secondary">{label}</Typography>
            <Typography variant="h4" fontWeight={800} color={isAlert ? "warning.main" : undefined}>{value}</Typography>
            {sub ? <Typography variant="caption" color="text.secondary">{sub}</Typography> : null}
        </Paper>
    );
}

function statusChip(status) {
    const map = {
        Active: "success",
        UnderMaintenance: "warning",
        Inactive: "default",
        Open: "error",
        InProgress: "warning",
        Completed: "success",
        Cancelled: "default",
        Good: "success",
        Fair: "info",
        Poor: "warning",
        Broken: "error",
        Disposed: "default",
    };
    return <Chip size="small" color={map[status] || "default"} label={status} />;
}

function priorityChip(p) {
    const map = { Urgent: "error", High: "warning", Medium: "info", Low: "default" };
    return <Chip size="small" color={map[p] || "default"} label={p} variant="outlined" />;
}

function fmtDate(d) {
    if (!d) return "—";
    try {
        return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch {
        return "—";
    }
}

const InfrastructureManagement = () => {
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = getSchoolIdForUser(currentUser);
    const adminName = currentUser?.name || "Admin";

    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [popupSeverity, setPopupSeverity] = useState(undefined);
    const [saving, setSaving] = useState(false);

    const [overview, setOverview] = useState(null);
    const [facilities, setFacilities] = useState([]);
    const [assets, setAssets] = useState([]);
    const [maintenance, setMaintenance] = useState([]);
    const [report, setReport] = useState(null);

    const [facSearch, setFacSearch] = useState("");
    const [facTypeFilter, setFacTypeFilter] = useState("");
    const [assetSearch, setAssetSearch] = useState("");
    const [assetCategory, setAssetCategory] = useState("");
    const [assetCondition, setAssetCondition] = useState("");
    const [maintStatus, setMaintStatus] = useState("all");
    const [maintSearch, setMaintSearch] = useState("");

    const [facilityDialog, setFacilityDialog] = useState(false);
    const [editingFacility, setEditingFacility] = useState(null);
    const [facilityForm, setFacilityForm] = useState(EMPTY_FACILITY);
    const [deleteFacilityTarget, setDeleteFacilityTarget] = useState(null);

    const [assetDialog, setAssetDialog] = useState(false);
    const [editingAsset, setEditingAsset] = useState(null);
    const [assetForm, setAssetForm] = useState(EMPTY_ASSET);
    const [deleteAssetTarget, setDeleteAssetTarget] = useState(null);

    const [maintDialog, setMaintDialog] = useState(false);
    const [maintForm, setMaintForm] = useState(EMPTY_MAINT);
    const [completeDialog, setCompleteDialog] = useState(null);
    const [completeForm, setCompleteForm] = useState({ completionNotes: "", conditionAfter: "" });

    const notify = (msg, severity) => {
        setMessage(msg);
        setPopupSeverity(severity);
        setShowPopup(true);
    };

    const loadOverview = useCallback(async () => {
        if (!schoolId) return;
        const { data } = await axios.get(buildApiUrl(`/InfrastructureOverview/${schoolId}`));
        setOverview(data);
    }, [schoolId]);

    const loadFacilities = useCallback(async () => {
        if (!schoolId) return;
        const params = new URLSearchParams();
        if (facSearch.trim()) params.set("q", facSearch.trim());
        if (facTypeFilter) params.set("facilityType", facTypeFilter);
        const qs = params.toString();
        const { data } = await axios.get(buildApiUrl(`/Facilities/${schoolId}${qs ? `?${qs}` : ""}`));
        setFacilities(Array.isArray(data) ? data : []);
    }, [schoolId, facSearch, facTypeFilter]);

    const loadAssets = useCallback(async () => {
        if (!schoolId) return;
        const params = new URLSearchParams();
        if (assetSearch.trim()) params.set("q", assetSearch.trim());
        if (assetCategory) params.set("category", assetCategory);
        if (assetCondition) params.set("condition", assetCondition);
        const qs = params.toString();
        const { data } = await axios.get(buildApiUrl(`/SchoolAssets/${schoolId}${qs ? `?${qs}` : ""}`));
        setAssets(Array.isArray(data) ? data : []);
    }, [schoolId, assetSearch, assetCategory, assetCondition]);

    const loadMaintenance = useCallback(async () => {
        if (!schoolId) return;
        const params = new URLSearchParams();
        if (maintStatus) params.set("status", maintStatus);
        if (maintSearch.trim()) params.set("q", maintSearch.trim());
        const qs = params.toString();
        const { data } = await axios.get(buildApiUrl(`/MaintenanceRequests/${schoolId}${qs ? `?${qs}` : ""}`));
        setMaintenance(Array.isArray(data) ? data : []);
    }, [schoolId, maintStatus, maintSearch]);

    const loadReport = useCallback(async () => {
        if (!schoolId) return;
        const { data } = await axios.get(buildApiUrl(`/InfrastructureReport/${schoolId}`));
        setReport(data);
    }, [schoolId]);

    const refreshAll = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        try {
            await Promise.all([loadOverview(), loadFacilities(), loadAssets(), loadMaintenance(), loadReport()]);
        } catch (err) {
            notify(err.response?.data?.message || "Could not load infrastructure data", "error");
        } finally {
            setLoading(false);
        }
    }, [schoolId, loadOverview, loadFacilities, loadAssets, loadMaintenance, loadReport]);

    useEffect(() => { refreshAll(); }, [refreshAll]);
    useEffect(() => { if (schoolId && tab === 0) loadFacilities(); }, [schoolId, tab, loadFacilities]);
    useEffect(() => { if (schoolId && tab === 1) loadAssets(); }, [schoolId, tab, loadAssets]);
    useEffect(() => { if (schoolId && tab === 2) loadMaintenance(); }, [schoolId, tab, loadMaintenance]);
    useEffect(() => { if (schoolId && tab === 3) loadReport(); }, [schoolId, tab, loadReport]);

    const facilityOptions = useMemo(
        () => facilities.map((f) => ({ id: f._id || f.id, name: f.name, type: f.facilityType })),
        [facilities],
    );

    const saveFacility = async () => {
        if (!facilityForm.name.trim()) { notify("Facility name is required", "error"); return; }
        setSaving(true);
        try {
            const payload = {
                schoolId,
                name: facilityForm.name.trim(),
                facilityType: facilityForm.facilityType,
                building: facilityForm.building.trim(),
                floor: facilityForm.floor.trim(),
                capacity: facilityForm.capacity === "" ? null : Number(facilityForm.capacity),
                status: facilityForm.status,
                description: facilityForm.description.trim(),
                notes: facilityForm.notes.trim(),
            };
            if (editingFacility) {
                await axios.put(buildApiUrl(`/Facility/${editingFacility._id || editingFacility.id}`), payload);
                notify("Facility updated", "success");
            } else {
                await axios.post(buildApiUrl("/Facility"), payload);
                notify("Facility added", "success");
            }
            setFacilityDialog(false);
            await refreshAll();
        } catch (err) {
            notify(err.response?.data?.message || "Could not save facility", "error");
        } finally {
            setSaving(false);
        }
    };

    const saveAsset = async () => {
        if (!assetForm.name.trim()) { notify("Asset name is required", "error"); return; }
        setSaving(true);
        try {
            const payload = {
                schoolId,
                name: assetForm.name.trim(),
                assetTag: assetForm.assetTag.trim(),
                category: assetForm.category,
                condition: assetForm.condition,
                quantity: Number(assetForm.quantity) || 1,
                facilityId: assetForm.facilityId || undefined,
                purchaseDate: assetForm.purchaseDate || undefined,
                purchaseValue: assetForm.purchaseValue === "" ? undefined : Number(assetForm.purchaseValue),
                manufacturer: assetForm.manufacturer.trim(),
                warrantyUntil: assetForm.warrantyUntil || undefined,
                description: assetForm.description.trim(),
            };
            if (editingAsset) {
                await axios.put(buildApiUrl(`/SchoolAsset/${editingAsset._id || editingAsset.id}`), payload);
                notify("Asset updated", "success");
            } else {
                await axios.post(buildApiUrl("/SchoolAsset"), payload);
                notify("Asset registered", "success");
            }
            setAssetDialog(false);
            await refreshAll();
        } catch (err) {
            notify(err.response?.data?.message || "Could not save asset", "error");
        } finally {
            setSaving(false);
        }
    };

    const saveMaintenance = async () => {
        if (!maintForm.title.trim()) { notify("Title is required", "error"); return; }
        setSaving(true);
        try {
            await axios.post(buildApiUrl("/MaintenanceRequest"), {
                schoolId,
                title: maintForm.title.trim(),
                description: maintForm.description.trim(),
                priority: maintForm.priority,
                facilityId: maintForm.facilityId || undefined,
                assetId: maintForm.assetId || undefined,
                assignedTo: maintForm.assignedTo.trim(),
                reportedBy: adminName,
            });
            notify("Maintenance request logged", "success");
            setMaintDialog(false);
            await refreshAll();
        } catch (err) {
            notify(err.response?.data?.message || "Could not create request", "error");
        } finally {
            setSaving(false);
        }
    };

    const setMaintStatusAction = async (row, status) => {
        const id = row._id || row.id;
        try {
            await axios.put(buildApiUrl(`/MaintenanceRequest/${id}`), { status });
            notify(`Status updated to ${status}`, "success");
            await refreshAll();
        } catch (err) {
            notify(err.response?.data?.message || "Could not update status", "error");
        }
    };

    const submitComplete = async () => {
        if (!completeDialog) return;
        const id = completeDialog._id || completeDialog.id;
        try {
            await axios.put(buildApiUrl(`/MaintenanceRequest/${id}/complete`), completeForm);
            notify("Maintenance marked complete", "success");
            setCompleteDialog(null);
            setCompleteForm({ completionNotes: "", conditionAfter: "" });
            await refreshAll();
        } catch (err) {
            notify(err.response?.data?.message || "Could not complete request", "error");
        }
    };

    const openEditFacility = (row) => {
        setEditingFacility(row);
        setFacilityForm({
            name: row.name || "",
            facilityType: row.facilityType || "Classroom",
            building: row.building || "",
            floor: row.floor || "",
            capacity: row.capacity != null ? String(row.capacity) : "",
            status: row.status || "Active",
            description: row.description || "",
            notes: row.notes || "",
        });
        setFacilityDialog(true);
    };

    const openEditAsset = (row) => {
        setEditingAsset(row);
        setAssetForm({
            name: row.name || "",
            assetTag: row.assetTag || "",
            category: row.category || "Equipment",
            condition: row.condition || "Good",
            quantity: String(row.quantity ?? 1),
            facilityId: row.facilityId || row.facility?.id || row.facility?._id || "",
            purchaseDate: row.purchaseDate ? String(row.purchaseDate).slice(0, 10) : "",
            purchaseValue: row.purchaseValue != null ? String(row.purchaseValue) : "",
            manufacturer: row.manufacturer || "",
            warrantyUntil: row.warrantyUntil ? String(row.warrantyUntil).slice(0, 10) : "",
            description: row.description || "",
        });
        setAssetDialog(true);
    };

    const alertCount = (overview?.openMaintenance || 0) + (overview?.assetsNeedingAttention || 0);

    return (
        <Box sx={{ minHeight: "100%", bgcolor: (t) => alpha(t.palette.primary.main, 0.03), pb: 4 }}>
            <Container maxWidth="lg" sx={{ pt: 3 }}>
                <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3 }, mb: 3, borderRadius: 3, background: (t) => `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.12)} 0%, ${alpha(t.palette.secondary.main, 0.08)} 100%)`, border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.15)}` }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
                        <Stack direction="row" spacing={2} alignItems="center">
                            <BusinessOutlinedIcon sx={{ fontSize: 40, color: "primary.main" }} />
                            <Box>
                                <Typography variant="h5" fontWeight={800}>Infrastructure & assets</Typography>
                                <Typography variant="body2" color="text.secondary">Facilities register, asset inventory, and maintenance tracking</Typography>
                            </Box>
                        </Stack>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Button variant="outlined" startIcon={<BuildOutlinedIcon />} onClick={() => { setMaintForm(EMPTY_MAINT); setMaintDialog(true); }}>Log maintenance</Button>
                            <Button variant="outlined" startIcon={<Inventory2OutlinedIcon />} onClick={() => { setEditingAsset(null); setAssetForm(EMPTY_ASSET); setAssetDialog(true); }}>Add asset</Button>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingFacility(null); setFacilityForm(EMPTY_FACILITY); setFacilityDialog(true); }}>Add facility</Button>
                        </Stack>
                    </Stack>
                </Paper>

                {alertCount > 0 ? (
                    <Alert severity="warning" icon={<WarningAmberOutlinedIcon />} sx={{ mb: 2, borderRadius: 2 }}>
                        {overview?.openMaintenance || 0} open maintenance request{(overview?.openMaintenance || 0) === 1 ? "" : "s"}
                        {(overview?.assetsNeedingAttention || 0) > 0 ? ` · ${overview.assetsNeedingAttention} asset(s) in poor/broken condition` : ""}.
                    </Alert>
                ) : null}

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={3}><Kpi label="Facilities" value={overview?.facilityCount ?? "—"} sub={`${overview?.facilitiesUnderMaint ?? 0} under maintenance`} /></Grid>
                    <Grid item xs={6} sm={3}><Kpi label="Assets" value={overview?.assetCount ?? "—"} sub="Registered items" /></Grid>
                    <Grid item xs={6} sm={3}><Kpi label="Open maintenance" value={overview?.openMaintenance ?? "—"} sub={`${overview?.inProgressMaintenance ?? 0} in progress`} alert={Number(overview?.openMaintenance) > 0} /></Grid>
                    <Grid item xs={6} sm={3}><Kpi label="Needs attention" value={overview?.assetsNeedingAttention ?? "—"} sub="Poor or broken assets" alert={Number(overview?.assetsNeedingAttention) > 0} /></Grid>
                </Grid>

                <Paper elevation={0} sx={{ borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}>
                        <Tab label="Facilities" icon={<BusinessOutlinedIcon fontSize="small" />} iconPosition="start" />
                        <Tab label="Assets" icon={<Inventory2OutlinedIcon fontSize="small" />} iconPosition="start" />
                        <Tab label="Maintenance" icon={<BuildOutlinedIcon fontSize="small" />} iconPosition="start" />
                        <Tab label="Reports" icon={<AssessmentOutlinedIcon fontSize="small" />} iconPosition="start" />
                    </Tabs>

                    {loading && tab === 0 ? <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress size={32} /></Box> : null}

                    {tab === 0 && !loading ? (
                        <Box sx={{ p: 2 }}>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
                                <TextField size="small" placeholder="Search facilities…" value={facSearch} onChange={(e) => setFacSearch(e.target.value)} InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }} sx={{ flex: 1 }} />
                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                    <InputLabel>Type</InputLabel>
                                    <Select label="Type" value={facTypeFilter} onChange={(e) => setFacTypeFilter(e.target.value)}>
                                        <MenuItem value="">All types</MenuItem>
                                        {FACILITY_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Stack>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Building / floor</TableCell>
                                            <TableCell align="right">Capacity</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="right">Assets</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {facilities.length === 0 ? (
                                            <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>No facilities yet. Add classrooms, labs, or dormitories.</TableCell></TableRow>
                                        ) : facilities.map((row) => {
                                            const id = row._id || row.id;
                                            return (
                                                <TableRow key={id} hover>
                                                    <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                                                    <TableCell>{row.facilityType}</TableCell>
                                                    <TableCell>{[row.building, row.floor].filter(Boolean).join(" · ") || "—"}</TableCell>
                                                    <TableCell align="right">{row.capacity ?? "—"}</TableCell>
                                                    <TableCell>{statusChip(row.status)}</TableCell>
                                                    <TableCell align="right">{row.assetCount ?? 0}</TableCell>
                                                    <TableCell align="right">
                                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                            <Button size="small" onClick={() => openEditFacility(row)}>Edit</Button>
                                                            <Button size="small" color="error" onClick={() => setDeleteFacilityTarget(row)}>Delete</Button>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ) : null}

                    {tab === 1 ? (
                        <Box sx={{ p: 2 }}>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
                                <TextField size="small" placeholder="Search assets…" value={assetSearch} onChange={(e) => setAssetSearch(e.target.value)} sx={{ flex: 1 }} />
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                    <InputLabel>Category</InputLabel>
                                    <Select label="Category" value={assetCategory} onChange={(e) => setAssetCategory(e.target.value)}>
                                        <MenuItem value="">All</MenuItem>
                                        {ASSET_CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                    <InputLabel>Condition</InputLabel>
                                    <Select label="Condition" value={assetCondition} onChange={(e) => setAssetCondition(e.target.value)}>
                                        <MenuItem value="">All</MenuItem>
                                        {ASSET_CONDITIONS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Stack>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Tag</TableCell>
                                            <TableCell>Category</TableCell>
                                            <TableCell>Location</TableCell>
                                            <TableCell>Condition</TableCell>
                                            <TableCell align="right">Qty</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {assets.length === 0 ? (
                                            <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>No assets registered.</TableCell></TableRow>
                                        ) : assets.map((row) => {
                                            const id = row._id || row.id;
                                            return (
                                                <TableRow key={id} hover>
                                                    <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                                                    <TableCell>{row.assetTag || "—"}</TableCell>
                                                    <TableCell>{row.category}</TableCell>
                                                    <TableCell>{row.facility?.name || "—"}</TableCell>
                                                    <TableCell>{statusChip(row.condition)}</TableCell>
                                                    <TableCell align="right">{row.quantity}</TableCell>
                                                    <TableCell align="right">
                                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                            <Button size="small" onClick={() => openEditAsset(row)}>Edit</Button>
                                                            <Button size="small" color="error" onClick={() => setDeleteAssetTarget(row)}>Delete</Button>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ) : null}

                    {tab === 2 ? (
                        <Box sx={{ p: 2 }}>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
                                <TextField size="small" placeholder="Search requests…" value={maintSearch} onChange={(e) => setMaintSearch(e.target.value)} sx={{ flex: 1 }} />
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                    <InputLabel>Status</InputLabel>
                                    <Select label="Status" value={maintStatus} onChange={(e) => setMaintStatus(e.target.value)}>
                                        <MenuItem value="all">All</MenuItem>
                                        {MAINT_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Stack>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Title</TableCell>
                                            <TableCell>Facility / asset</TableCell>
                                            <TableCell>Priority</TableCell>
                                            <TableCell>Assigned</TableCell>
                                            <TableCell>Requested</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {maintenance.length === 0 ? (
                                            <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>No maintenance requests.</TableCell></TableRow>
                                        ) : maintenance.map((row) => {
                                            const id = row._id || row.id;
                                            const loc = row.facility?.name || row.asset?.name || "—";
                                            return (
                                                <TableRow key={id} hover>
                                                    <TableCell sx={{ fontWeight: 600 }}>{row.title}</TableCell>
                                                    <TableCell>{loc}</TableCell>
                                                    <TableCell>{priorityChip(row.priority)}</TableCell>
                                                    <TableCell>{row.assignedTo || "—"}</TableCell>
                                                    <TableCell>{fmtDate(row.requestedAt)}</TableCell>
                                                    <TableCell>{statusChip(row.status)}</TableCell>
                                                    <TableCell align="right">
                                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                                                            {row.status === "Open" ? (
                                                                <Button size="small" onClick={() => setMaintStatusAction(row, "InProgress")}>Start</Button>
                                                            ) : null}
                                                            {["Open", "InProgress"].includes(row.status) ? (
                                                                <Button size="small" variant="outlined" onClick={() => { setCompleteDialog(row); setCompleteForm({ completionNotes: "", conditionAfter: row.asset ? row.asset.condition : "" }); }}>Complete</Button>
                                                            ) : null}
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ) : null}

                    {tab === 3 && report ? (
                        <Box sx={{ p: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Generated {fmtDate(report.generatedAt)}</Typography>

                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Asset condition summary</Typography>
                            <TableContainer sx={{ mb: 3 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Condition</TableCell>
                                            <TableCell align="right">Count</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(report.conditionSummary || []).map((r) => (
                                            <TableRow key={r.condition}>
                                                <TableCell>{statusChip(r.condition)}</TableCell>
                                                <TableCell align="right">{r.count}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Assets needing attention</Typography>
                            {(report.assetsNeedingAttention || []).length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>All assets in good or fair condition.</Typography>
                            ) : (
                                <TableContainer sx={{ mb: 3 }}>
                                    <Table size="small">
                                        <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Category</TableCell><TableCell>Condition</TableCell><TableCell>Location</TableCell></TableRow></TableHead>
                                        <TableBody>
                                            {report.assetsNeedingAttention.map((a) => (
                                                <TableRow key={a._id || a.id}>
                                                    <TableCell>{a.name}</TableCell>
                                                    <TableCell>{a.category}</TableCell>
                                                    <TableCell>{statusChip(a.condition)}</TableCell>
                                                    <TableCell>{a.facility?.name || "—"}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Open maintenance</Typography>
                            {(report.openMaintenance || []).length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No open work orders.</Typography>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead><TableRow><TableCell>Title</TableCell><TableCell>Priority</TableCell><TableCell>Status</TableCell><TableCell>Requested</TableCell></TableRow></TableHead>
                                        <TableBody>
                                            {report.openMaintenance.map((m) => (
                                                <TableRow key={m._id || m.id}>
                                                    <TableCell>{m.title}</TableCell>
                                                    <TableCell>{priorityChip(m.priority)}</TableCell>
                                                    <TableCell>{statusChip(m.status)}</TableCell>
                                                    <TableCell>{fmtDate(m.requestedAt)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    ) : null}
                </Paper>
            </Container>

            {/* Facility dialog */}
            <Dialog open={facilityDialog} onClose={() => setFacilityDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingFacility ? "Edit facility" : "Add facility"}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}><TextField fullWidth required label="Name" value={facilityForm.name} onChange={(e) => setFacilityForm({ ...facilityForm, name: e.target.value })} /></Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth><InputLabel>Type</InputLabel>
                                <Select label="Type" value={facilityForm.facilityType} onChange={(e) => setFacilityForm({ ...facilityForm, facilityType: e.target.value })}>
                                    {FACILITY_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth><InputLabel>Status</InputLabel>
                                <Select label="Status" value={facilityForm.status} onChange={(e) => setFacilityForm({ ...facilityForm, status: e.target.value })}>
                                    {FACILITY_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Building / block" value={facilityForm.building} onChange={(e) => setFacilityForm({ ...facilityForm, building: e.target.value })} /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Floor" value={facilityForm.floor} onChange={(e) => setFacilityForm({ ...facilityForm, floor: e.target.value })} /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth type="number" label="Capacity" value={facilityForm.capacity} onChange={(e) => setFacilityForm({ ...facilityForm, capacity: e.target.value })} /></Grid>
                        <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Description" value={facilityForm.description} onChange={(e) => setFacilityForm({ ...facilityForm, description: e.target.value })} /></Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFacilityDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveFacility} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
                </DialogActions>
            </Dialog>

            {/* Asset dialog */}
            <Dialog open={assetDialog} onClose={() => setAssetDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingAsset ? "Edit asset" : "Register asset"}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}><TextField fullWidth required label="Name" value={assetForm.name} onChange={(e) => setAssetForm({ ...assetForm, name: e.target.value })} /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Asset tag / serial" value={assetForm.assetTag} onChange={(e) => setAssetForm({ ...assetForm, assetTag: e.target.value })} /></Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth><InputLabel>Category</InputLabel>
                                <Select label="Category" value={assetForm.category} onChange={(e) => setAssetForm({ ...assetForm, category: e.target.value })}>
                                    {ASSET_CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth><InputLabel>Condition</InputLabel>
                                <Select label="Condition" value={assetForm.condition} onChange={(e) => setAssetForm({ ...assetForm, condition: e.target.value })}>
                                    {ASSET_CONDITIONS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth type="number" label="Quantity" value={assetForm.quantity} onChange={(e) => setAssetForm({ ...assetForm, quantity: e.target.value })} inputProps={{ min: 1 }} /></Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth><InputLabel>Facility location</InputLabel>
                                <Select label="Facility location" value={assetForm.facilityId} onChange={(e) => setAssetForm({ ...assetForm, facilityId: e.target.value })}>
                                    <MenuItem value="">— Not assigned —</MenuItem>
                                    {facilityOptions.map((f) => <MenuItem key={f.id} value={f.id}>{f.name} ({f.type})</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Manufacturer" value={assetForm.manufacturer} onChange={(e) => setAssetForm({ ...assetForm, manufacturer: e.target.value })} /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Purchase date" InputLabelProps={{ shrink: true }} value={assetForm.purchaseDate} onChange={(e) => setAssetForm({ ...assetForm, purchaseDate: e.target.value })} /></Grid>
                        <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Description" value={assetForm.description} onChange={(e) => setAssetForm({ ...assetForm, description: e.target.value })} /></Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssetDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveAsset} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
                </DialogActions>
            </Dialog>

            {/* Maintenance dialog */}
            <Dialog open={maintDialog} onClose={() => setMaintDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Log maintenance request</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}><TextField fullWidth required label="Title" value={maintForm.title} onChange={(e) => setMaintForm({ ...maintForm, title: e.target.value })} /></Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth><InputLabel>Priority</InputLabel>
                                <Select label="Priority" value={maintForm.priority} onChange={(e) => setMaintForm({ ...maintForm, priority: e.target.value })}>
                                    {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Assign to" value={maintForm.assignedTo} onChange={(e) => setMaintForm({ ...maintForm, assignedTo: e.target.value })} placeholder="e.g. caretaker, IT technician" /></Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth><InputLabel>Facility (optional)</InputLabel>
                                <Select label="Facility (optional)" value={maintForm.facilityId} onChange={(e) => setMaintForm({ ...maintForm, facilityId: e.target.value })}>
                                    <MenuItem value="">—</MenuItem>
                                    {facilityOptions.map((f) => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth><InputLabel>Asset (optional)</InputLabel>
                                <Select label="Asset (optional)" value={maintForm.assetId} onChange={(e) => setMaintForm({ ...maintForm, assetId: e.target.value })}>
                                    <MenuItem value="">—</MenuItem>
                                    {assets.map((a) => { const id = a._id || a.id; return <MenuItem key={id} value={id}>{a.name}</MenuItem>; })}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Description" value={maintForm.description} onChange={(e) => setMaintForm({ ...maintForm, description: e.target.value })} /></Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMaintDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveMaintenance} disabled={saving}>{saving ? "Saving…" : "Submit"}</Button>
                </DialogActions>
            </Dialog>

            {/* Complete maintenance dialog */}
            <Dialog open={Boolean(completeDialog)} onClose={() => setCompleteDialog(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Complete maintenance</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField fullWidth multiline minRows={2} label="Completion notes" value={completeForm.completionNotes} onChange={(e) => setCompleteForm({ ...completeForm, completionNotes: e.target.value })} />
                        {completeDialog?.asset ? (
                            <FormControl fullWidth>
                                <InputLabel>Asset condition after repair</InputLabel>
                                <Select label="Asset condition after repair" value={completeForm.conditionAfter} onChange={(e) => setCompleteForm({ ...completeForm, conditionAfter: e.target.value })}>
                                    <MenuItem value="">— No change —</MenuItem>
                                    {ASSET_CONDITIONS.filter((c) => c !== "Disposed").map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                </Select>
                            </FormControl>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCompleteDialog(null)}>Cancel</Button>
                    <Button variant="contained" onClick={submitComplete}>Mark complete</Button>
                </DialogActions>
            </Dialog>

            <ConfirmDeleteDialog open={Boolean(deleteFacilityTarget)} onClose={() => setDeleteFacilityTarget(null)} onConfirm={async () => {
                try {
                    await axios.delete(buildApiUrl(`/Facility/${deleteFacilityTarget._id || deleteFacilityTarget.id}`));
                    notify("Facility deleted", "success");
                    setDeleteFacilityTarget(null);
                    await refreshAll();
                } catch (err) {
                    notify(err.response?.data?.message || "Could not delete", "error");
                    setDeleteFacilityTarget(null);
                }
            }} title="Delete facility?" message={`Remove "${deleteFacilityTarget?.name}"? Assets will be unlinked.`} />

            <ConfirmDeleteDialog open={Boolean(deleteAssetTarget)} onClose={() => setDeleteAssetTarget(null)} onConfirm={async () => {
                try {
                    await axios.delete(buildApiUrl(`/SchoolAsset/${deleteAssetTarget._id || deleteAssetTarget.id}`));
                    notify("Asset deleted", "success");
                    setDeleteAssetTarget(null);
                    await refreshAll();
                } catch (err) {
                    notify(err.response?.data?.message || "Could not delete", "error");
                    setDeleteAssetTarget(null);
                }
            }} title="Delete asset?" message={`Remove "${deleteAssetTarget?.name}" from the register?`} />

            <Popup showPopup={showPopup} setShowPopup={setShowPopup} message={message} severity={popupSeverity} />
        </Box>
    );
};

export default InfrastructureManagement;
